import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer } from "lucide-react";
export default function QRCodesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [layout, setLayout] = useState("badges");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = clientFilter !== "all" ? { client_id: clientFilter } : {};
    api.get("/qr/bulk", { params }).then((r) => {
      setItems(r.data);
      setSelected(new Set(r.data.map((i) => i.id)));
      setLoading(false);
    });
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.role === "super_admin") api.get("/clients").then((r) => setClients(r.data));
  }, [user]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const teachers = [...new Set(items.map((i) => i.teacher).filter(Boolean))].sort();
  const visible = classFilter === "all" ? items : items.filter((i) => i.teacher === classFilter);
  const printable = visible.filter((i) => selected.has(i.id));

  const changeClass = (v) => {
    setClassFilter(v);
    const next = v === "all" ? items : items.filter((i) => i.teacher === v);
    setSelected(new Set(next.map((i) => i.id)));
  };

  return (
    <div className="space-y-6 fade-up" data-testid="qr-codes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Badging</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">QR Code Management</h1>
          <p className="mt-1 text-sm text-slate-500">Printable badges · {printable.length} of {visible.length} selected</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "super_admin" && (
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-56 bg-white" data-testid="qr-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={classFilter} onValueChange={changeClass}>
            <SelectTrigger className="w-48 bg-white" data-testid="qr-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {teachers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={layout} onValueChange={setLayout}>
            <SelectTrigger className="w-64 bg-white" data-testid="print-layout-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="badges">Badge cards (3 per row)</SelectItem>
              <SelectItem value="avery">Avery 5163 stickers (2" × 4", 10/sheet)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-full" data-testid="select-all-button"
            onClick={() => setSelected(printable.length === visible.length ? new Set() : new Set(visible.map((i) => i.id)))}>
            {printable.length === visible.length && visible.length > 0 ? "Deselect all" : "Select all"}
          </Button>
          <Button onClick={() => window.print()} disabled={printable.length === 0} data-testid="print-badges-button"
            className="rounded-full bg-primary hover:bg-primary/90">
            <Printer size={15} className="mr-1.5" /> {layout === "avery" ? "Print Stickers" : "Print Badges"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map((i) => (
            <div key={i.id}
              className={`card-lift relative rounded-lg border bg-white p-4 shadow-soft transition-colors duration-200 ${selected.has(i.id) ? "border-primary" : "border-slate-200"}`}
              data-testid={`qr-card-${i.student_id}`}>
              <div className="absolute right-3 top-3">
                <Checkbox checked={selected.has(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`qr-select-${i.student_id}`} />
              </div>
              <div className="flex items-center gap-1.5 text-slate-900">
                <img src="/tagverify-icon.png" alt="" className="h-4 w-4 object-contain" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em]">TAGVerify Badge</span>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <img src={`data:image/png;base64,${i.qr}`} alt="QR" className="h-24 w-24 rounded border border-slate-100" />
                <div className="min-w-0">
                  <div className="truncate font-display font-semibold text-slate-900">{i.first_name} {i.last_name}</div>
                  <div className="font-mono text-xs text-slate-500">{i.student_id}</div>
                  <div className="mt-1 text-xs text-slate-500">Grade {i.grade}{i.teacher ? ` · ${i.teacher}` : ""}</div>
                  <div className="truncate text-xs text-slate-400">{i.school_name}</div>
                </div>
              </div>
            </div>
          ))}
          {visible.length === 0 && <div className="text-slate-400">No students to display</div>}
        </div>
      )}

      {/* print-only sheet */}
      <div className={`print-area hidden ${layout === "avery" ? "avery-5163" : "badge-sheet"}`}>
        {layout === "avery"
          ? printable.map((i) => (
              <div key={i.id} className="avery-label">
                <img src={`data:image/png;base64,${i.qr}`} alt="QR" className="avery-qr" />
                <div className="avery-text">
                  <div className="font-display text-base font-bold leading-tight">{i.first_name} {i.last_name}</div>
                  <div className="font-mono text-xs">{i.student_id} · Grade {i.grade}</div>
                  <div className="text-xs text-slate-600">{i.teacher ? `${i.teacher} · ` : ""}{i.school_name}</div>
                  <img src="/tagverify-logo.png" alt="TAGVerify" className="mt-1 h-4 w-auto object-contain" />
                </div>
              </div>
            ))
          : printable.map((i) => (
              <div key={i.id} className="print-badge rounded-lg border border-slate-300 p-4 text-center">
                <div className="mb-1 flex items-center justify-center">
                  <img src="/tagverify-logo.png" alt="TAGVerify" className="h-5 w-auto object-contain" />
                </div>
                <img src={`data:image/png;base64,${i.qr}`} alt="QR" className="mx-auto h-32 w-32" />
                <div className="mt-1 font-display text-base font-bold">{i.first_name} {i.last_name}</div>
                <div className="font-mono text-xs">{i.student_id} · Grade {i.grade}</div>
                <div className="text-xs text-slate-600">{i.teacher ? `${i.teacher} · ` : ""}{i.school_name}</div>
                <div className="text-[10px] text-slate-400">{i.client_name}</div>
              </div>
            ))}
      </div>
    </div>
  );
}
